// ============================================================================
// JIRA SMART ISSUE CREATE
// ============================================================================
import {
	JIRA_EPICS_SUGGEST_KEY,
	JIRA_FIND_ISSUES_KEY,
	JIRA_HAM_AI_INSIGHTS_PREVIEW_KEY,
	JIRA_ISSUE_ASSIGN_KEY,
	JIRA_ISSUE_DELETE_KEY,
	JIRA_ISSUE_MOVE_KEY,
	JIRA_ISSUE_TRANSITION_KEY,
	JIRA_SMART_ISSUE_CREATE_ALL_FIELDS_KEY,
	JIRA_VERSION_CREATE_KEY,
	JSM_CREATE_EMBEDDED_REQUEST_KEY,
	JSM_CREATE_REQUEST_KEY,
	JSM_CREATE_REQUEST_V2_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { JIRA_SMART_CREATE_TABLE_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/jira-smart-create-modal';

export const JIRA_ACTION_KEYS: string[] = [
	JIRA_FIND_ISSUES_KEY,
	JIRA_SMART_ISSUE_CREATE_ALL_FIELDS_KEY,
	JSM_CREATE_REQUEST_KEY,
	JSM_CREATE_REQUEST_V2_KEY,
	JSM_CREATE_EMBEDDED_REQUEST_KEY,
	JIRA_ISSUE_TRANSITION_KEY,
	JIRA_EPICS_SUGGEST_KEY,
	JIRA_ISSUE_DELETE_KEY,
	JIRA_ISSUE_MOVE_KEY,
	JIRA_ISSUE_ASSIGN_KEY,
	JIRA_VERSION_CREATE_KEY,
	JIRA_HAM_AI_INSIGHTS_PREVIEW_KEY,
];

export const JIRA_ACTION_KEYS_SET: Set<string> = new Set<string>(JIRA_ACTION_KEYS);

export const JIRA_CREATABLE_ACTION_KEYS_SET: Set<string> = new Set([
	JIRA_SMART_ISSUE_CREATE_ALL_FIELDS_KEY,
]);

/**
 * Action keys that should open the staging area for preview but do not create content.
 * These are distinct from creatable actions and should not trigger Jira AIFC create flows.
 */
export const JIRA_PREVIEW_ONLY_ACTION_KEYS_SET: Set<string> = new Set([
	JIRA_HAM_AI_INSIGHTS_PREVIEW_KEY,
]);

export const isJiraAction = (action: unknown): action is MessageAction => {
	return (
		typeof action === 'object' &&
		action !== null &&
		'key' in action &&
		JIRA_ACTION_KEYS_SET.has(action?.['key'] as string)
	);
};

export const isJiraCreatableAction = (action: unknown): action is MessageAction =>
	isJiraAction(action) && JIRA_CREATABLE_ACTION_KEYS_SET.has(action.key);

/**
 * Returns true for Jira actions that should open the staging area, including
 * both content-creating actions and preview-only actions (e.g. HAM AI insights).
 */
export const isJiraPreviewableAction = (action: unknown): action is MessageAction =>
	isJiraAction(action) && JIRA_PREVIEW_ONLY_ACTION_KEYS_SET.has(action.key);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isJiraUpdatableAction = (action: { key: string }): boolean => false;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isJiraStreamAction = (action: { key: string }): boolean => false;

export const generateJiraReceiverId = (action: unknown): string | undefined => {
	if (!isJiraAction(action)) {
		return undefined;
	}

	if (isJiraCreatableAction(action)) {
		return JIRA_SMART_CREATE_TABLE_CLIENT_ID;
	}

	return undefined;
};

export const getJiraContentType = (action: { key: string }): 'jira-issue-table' | undefined => {
	const actionKey = action.key;
	if (JIRA_CREATABLE_ACTION_KEYS_SET.has(actionKey)) {
		return 'jira-issue-table';
	}
	return undefined;
};

// ============================================================================
// JIRA PREVIEW CARDS
// ============================================================================
const PREVIEW_CARD_ACTION_KEYS = new Set([JIRA_SMART_ISSUE_CREATE_ALL_FIELDS_KEY]);

export const isJiraPreviewCardAction = (action: { key: string }): boolean =>
	PREVIEW_CARD_ACTION_KEYS.has(action.key);
